import type { IAppSummary } from '~/types/application'
import type { IAsset } from '~/types/assets-management'
import type { IPageContainer, IPageContainerDetails } from '~/types/page-container'

import { useAssetDetailsQueries } from '~/queries/assets'
import { useIapListBySkuQuery } from '~/queries/iap'
import { useTranslationValuesQuery } from '~/queries/translation'
import { formatPageContainerData } from '~/utils/page-container'

export const usePageContainerDetails = ({
  pageContainer,
  selectedApp,
}: {
  pageContainer: IPageContainer
  selectedApp: IAppSummary
}): { isLoading: boolean; pageContainerDetails: IPageContainerDetails } => {
  const skus: string[] =
    pageContainer.placeholder_iap_values?.reduce((prev, { id }) => {
      if (id) prev.push(id)
      return prev
    }, [] as string[]) || []

  const { data: iaps, isLoading: isIapListLoading } = useIapListBySkuQuery(
    {
      appKey: selectedApp.app_key,
      deviceType: selectedApp.device_type,
      skus,
    },
    { enabled: !!selectedApp.app_key && !!selectedApp.device_type && !!skus.length }
  )

  const assetIds = pageContainer?.placeholder_asset_values?.map(({ id }) => id || '')

  const assetQueries = useAssetDetailsQueries(assetIds)

  const assets = assetQueries.some((e) => e.isLoading) ? [] : assetQueries.map(({ data }) => data as IAsset)

  const translationKeys = pageContainer?.placeholder_text_values?.map(({ translation_key }) => translation_key) || []

  const { data: translationsData } = useTranslationValuesQuery({ application: selectedApp, translationKeys })

  const pageContainerDetails = formatPageContainerData({
    assets,
    iaps,
    pageContainer,
    translations: translationsData || [],
  })

  return {
    isLoading: !!(isIapListLoading && skus.length) || assetQueries.some((e) => e.isLoading),
    pageContainerDetails,
  }
}
